/**
 * Hook for managing workflow node fields
 *
 * Provides utilities for:
 * - Getting merged input/output fields (defaults + custom)
 * - Managing input field mappings
 * - Auto-generating mappings from edges
 * - Finding available source nodes and outputs
 */

import { useMemo, useCallback } from 'react';
import { useWorkflowStore } from './useWorkflowStore';
import type {
  FieldDefinition,
  OutputFieldDefinition,
  InputFieldMapping,
  NodeTypeSpec,
  FieldType,
  AvailableSource,
  AvailableSourceOutput,
} from '@/types/workflowFields';
import { LITERAL_SOURCE_KEY } from '@/types/workflowFields';
import type { NodeType } from '@/types/imageWorkflow';
import { reactFlowIdToKey } from '@/types/imageWorkflow';

// ============================================================================
// Node Type Definitions (Client-side defaults, mirrors server registry)
// ============================================================================

const NODE_TYPE_SPECS: Record<string, NodeTypeSpec> = {
  start: {
    node_type: 'start',
    label: 'Start',
    description: 'Workflow entry point',
    category: 'control',
    default_inputs: [],
    default_outputs: [
      { key: 'context', type: 'object', label: 'Context', required: true },
      { key: 'variables', type: 'object', label: 'Variables', required: true },
    ],
  },
  prompt: {
    node_type: 'prompt',
    label: 'Prompt',
    description: 'Generate or input text prompts',
    category: 'generation',
    default_inputs: [
      { key: 'context', type: 'object', label: 'Context', required: false },
    ],
    default_outputs: [
      // Manual mode: only prompt field is input/output
      // Agent/LLM mode: use output_schema to define output fields
      // Custom outputs can be added via the + button
      { key: 'prompt', type: 'string', label: '图像描述', required: true },
    ],
    allows_custom_outputs: true,
  },
  generate: {
    node_type: 'generate',
    label: 'Generate',
    description: 'Generate image from prompt',
    category: 'image',
    default_inputs: [
      { key: 'prompt', type: 'string', label: 'Prompt', required: true },
      { key: 'context', type: 'object', label: 'Context', required: false },
    ],
    default_outputs: [
      { key: 'image_url', type: 'image_url', label: 'Image URL', required: true },
      { key: 'task_id', type: 'string', label: 'Task ID', required: true },
      { key: 'model', type: 'string', label: 'Model', required: false },
    ],
  },
  edit: {
    node_type: 'edit',
    label: 'Edit',
    description: 'Edit existing image with prompt',
    category: 'image',
    default_inputs: [
      { key: 'prompt', type: 'string', label: 'Prompt', required: true },
      { key: 'image_url', type: 'image_url', label: 'Source Image', required: true },
      { key: 'context', type: 'object', label: 'Context', required: false },
    ],
    default_outputs: [
      { key: 'image_url', type: 'image_url', label: 'Image URL', required: true },
      { key: 'task_id', type: 'string', label: 'Task ID', required: true },
    ],
  },
  fuse: {
    node_type: 'fuse',
    label: 'Fuse',
    description: 'Combine multiple images',
    category: 'image',
    default_inputs: [
      { key: 'prompt', type: 'string', label: 'Prompt', required: true },
      { key: 'image', type: 'image_url', label: 'Image', required: true, indexed: true, max_connections: 10 },
      { key: 'context', type: 'object', label: 'Context', required: false },
    ],
    default_outputs: [
      { key: 'image_url', type: 'image_url', label: 'Image URL', required: true },
      { key: 'task_id', type: 'string', label: 'Task ID', required: true },
    ],
    supports_indexed_inputs: true,
  },
  lucky_charm: {
    node_type: 'lucky_charm',
    label: 'Lucky Charm',
    description: 'Generate fortune lucky charm image',
    category: 'special',
    default_inputs: [
      { key: 'phrase', type: 'string', label: 'Phrase', required: true },
      { key: 'context', type: 'object', label: 'Context', required: false },
    ],
    default_outputs: [
      { key: 'image_url', type: 'image_url', label: 'Image URL', required: true },
      { key: 'phrase', type: 'string', label: 'Phrase', required: true },
      { key: 'mode', type: 'string', label: 'Mode', required: false },
    ],
  },
  push: {
    node_type: 'push',
    label: 'Push',
    description: 'Send push notifications',
    category: 'notification',
    default_inputs: [
      { key: 'title', type: 'string', label: 'Title', required: true },
      { key: 'body', type: 'string', label: 'Body', required: true },
      { key: 'image_url', type: 'image_url', label: 'Image', required: false },
      { key: 'context', type: 'object', label: 'Context', required: false },
    ],
    // Terminal node - no outputs needed (results shown in execution panel)
    default_outputs: [],
    allows_custom_outputs: false,
  },
  output: {
    node_type: 'output',
    label: 'Output',
    description: 'Save workflow output',
    category: 'output',
    default_inputs: [
      { key: 'image_url', type: 'image_url', label: 'Image', required: true },
      { key: 'context', type: 'object', label: 'Context', required: false },
    ],
    default_outputs: [
      { key: 'image_url', type: 'image_url', label: 'Image URL', required: true },
      { key: 'saved_to_library', type: 'boolean', label: 'Saved to Library', required: true },
      { key: 'artifact_id', type: 'string', label: 'Artifact ID', required: false },
    ],
  },
};

// ============================================================================
// Hook
// ============================================================================

export function useNodeFields(nodeId: string | null) {
  const nodes = useWorkflowStore((state) => state.nodes);
  const edges = useWorkflowStore((state) => state.edges);
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);

  // Get the current node
  const node = useMemo(() => {
    if (!nodeId) return null;
    return nodes.find((n) => n.id === nodeId);
  }, [nodes, nodeId]);

  const nodeKey = node?.data?.nodeKey || (nodeId ? reactFlowIdToKey(nodeId) : null);
  const nodeType = node?.type as NodeType | undefined;
  const nodeData = node?.data;

  // Get node type spec
  const nodeTypeSpec = useMemo(() => {
    if (!nodeType) return null;
    return NODE_TYPE_SPECS[nodeType] || null;
  }, [nodeType]);

  // Get custom input fields only (user-defined, not from node type spec)
  const customInputFields = useMemo((): FieldDefinition[] => {
    return nodeData?.input_fields || [];
  }, [nodeData?.input_fields]);

  // Get merged input fields (defaults + custom)
  const inputFields = useMemo((): FieldDefinition[] => {
    if (!nodeTypeSpec) return [];

    const defaults = nodeTypeSpec.default_inputs;
    const customs = customInputFields;

    if (customs.length === 0) return defaults;

    // Custom fields override defaults with the same key
    const customKeys = new Set(customs.map((f) => f.key));
    const filtered = defaults.filter((f) => !customKeys.has(f.key));

    return [...filtered, ...customs];
  }, [nodeTypeSpec, customInputFields]);

  // Get custom output fields only (user-defined, not from node type spec)
  const customOutputFields = useMemo((): OutputFieldDefinition[] => {
    return nodeData?.output_fields || [];
  }, [nodeData?.output_fields]);

  // Get schema-derived output fields (from output_schema selection)
  const schemaOutputFields = useMemo((): OutputFieldDefinition[] => {
    return (nodeData?.schema_output_fields || []) as OutputFieldDefinition[];
  }, [nodeData?.schema_output_fields]);

  // Get merged output fields with proper override logic:
  // 1. Schema fields completely REPLACE defaults (if any exist)
  // 2. Custom fields are added (not overwriting schema/defaults)
  const outputFields = useMemo((): OutputFieldDefinition[] => {
    if (!nodeTypeSpec) return [];

    const hasSchemaFields = schemaOutputFields.length > 0;
    const baseFields = hasSchemaFields ? schemaOutputFields : nodeTypeSpec.default_outputs;
    const baseKeys = new Set(baseFields.map((f) => f.key));

    // Custom fields that don't overlap with base fields
    const extraCustoms = customOutputFields.filter((f) => !baseKeys.has(f.key));

    return [...baseFields, ...extraCustoms];
  }, [nodeTypeSpec, schemaOutputFields, customOutputFields]);

  // Get current input mappings
  const inputMappings = useMemo((): InputFieldMapping[] => {
    return nodeData?.input_mappings || [];
  }, [nodeData?.input_mappings]);

  // Find available source nodes for this node
  const availableSources = useMemo((): AvailableSource[] => {
    if (!nodeKey) return [];

    // Find incoming edges
    const incoming = edges.filter((e) => {
      const targetKey = reactFlowIdToKey(e.target);
      return targetKey === nodeKey;
    });

    return incoming
      .map((edge) => {
        const sourceKey = reactFlowIdToKey(edge.source);
        const sourceNode = nodes.find((n) => reactFlowIdToKey(n.id) === sourceKey);
        if (!sourceNode) return null;

        const sourceType = sourceNode.type as NodeType;
        const sourceSpec = NODE_TYPE_SPECS[sourceType];
        const sourceData = sourceNode.data;

        // Get source's output fields with proper override logic:
        // 1. Schema fields completely REPLACE defaults (if any exist)
        // 2. Custom fields are added (not overwriting schema/defaults)
        const schemaOutputs = (sourceData?.schema_output_fields || []) as OutputFieldDefinition[];
        const defaultOutputs = sourceSpec?.default_outputs || [];
        const customOutputs = (sourceData?.output_fields || []) as OutputFieldDefinition[];

        // Determine base outputs: schema fields override defaults entirely
        const hasSchemaOutputs = schemaOutputs.length > 0;
        const baseOutputs = hasSchemaOutputs ? schemaOutputs : defaultOutputs;

        // Get custom fields that don't overlap with base outputs
        const baseKeys = new Set(baseOutputs.map((o) => o.key));
        const extraCustoms = customOutputs.filter((o) => !baseKeys.has(o.key));

        // Get input fields for looking up object_keys on passthrough outputs
        const sourceInputFields = (sourceData?.input_fields || []) as FieldDefinition[];

        const outputs: AvailableSourceOutput[] = [
          ...baseOutputs.map((o) => ({
            key: o.key,
            type: o.type as FieldType,
            label: o.label,
            ...(o.object_keys && { object_keys: o.object_keys }),
          })),
          ...extraCustoms.map((o) => {
            // For passthrough outputs (with value_source_input_key), look up object_keys from the source input field
            let objectKeys = o.object_keys;
            if (o.value_source_input_key && !objectKeys) {
              const sourceInput = sourceInputFields.find(
                (f) => f.key === o.value_source_input_key
              );
              objectKeys = sourceInput?.object_keys;
            }
            return {
              key: o.key,
              type: o.type as FieldType,
              label: o.label,
              ...(objectKeys && { object_keys: objectKeys }),
            };
          }),
        ];

        return {
          node_key: sourceKey,
          node_type: sourceType as string,
          node_label: (sourceData?.label as string) || sourceKey,
          outputs,
        };
      })
      .filter((s): s is AvailableSource => s !== null);
  }, [nodeKey, edges, nodes]);

  // Auto-generate mappings based on smart matching (for existing input fields)
  const autoGenerateMappings = useCallback((): InputFieldMapping[] => {
    if (!nodeKey || inputFields.length === 0) return [];

    const mappings: InputFieldMapping[] = [];
    const indexedCounters: Record<string, number> = {};

    for (const source of availableSources) {
      for (const inputField of inputFields) {
        const fieldKey = inputField.key;
        const isIndexed = inputField.indexed || false;

        // Check if source has matching output
        for (const output of source.outputs) {
          let matched = false;

          // Exact key match
          if (output.key === fieldKey) {
            matched = true;
          }
          // Type-based matching for image_url
          else if (inputField.type === 'image_url' && output.type === 'image_url') {
            matched = true;
          }

          if (matched) {
            if (isIndexed) {
              const idx = (indexedCounters[fieldKey] || 0) + 1;
              indexedCounters[fieldKey] = idx;
              mappings.push({
                input_field_key: `${fieldKey}_${idx}`,
                source_node_key: source.node_key,
                source_output_key: output.key,
                is_auto_mapped: true,
              });
            } else {
              // Only add if not already mapped
              const existing = mappings.find((m) => m.input_field_key === fieldKey);
              if (!existing) {
                mappings.push({
                  input_field_key: fieldKey,
                  source_node_key: source.node_key,
                  source_output_key: output.key,
                  is_auto_mapped: true,
                });
              }
            }
            break;
          }
        }
      }
    }

    return mappings;
  }, [nodeKey, inputFields, availableSources]);

  // Auto-map from upstream: create new input fields and mappings from upstream outputs
  // IMPORTANT:
  // - This only adds NEW custom input fields and mappings
  // - Never overwrites existing user-defined fields or mappings
  // - Maps ALL upstream outputs to the "Input Fields" section
  // - The "Config Options" section should NOT read these auto-mappings (is_auto_mapped: true)
  const autoMapFromUpstream = useCallback((): {
    newFields: FieldDefinition[];
    newMappings: InputFieldMapping[];
  } => {
    if (!nodeKey || availableSources.length === 0) {
      return { newFields: [], newMappings: [] };
    }

    const newFields: FieldDefinition[] = [];
    const newMappings: InputFieldMapping[] = [];
    const existingFieldKeys = new Set(customInputFields.map((f) => f.key));
    const existingMappingKeys = new Set(inputMappings.map((m) => m.input_field_key));

    // Collect all upstream outputs and create corresponding input fields
    for (const source of availableSources) {
      for (const output of source.outputs) {
        // Skip 'context' and 'variables' outputs as they are system fields
        if (output.key === 'context' || output.key === 'variables') {
          continue;
        }

        // Check if field already exists - NEVER overwrite user-defined fields
        if (!existingFieldKeys.has(output.key)) {
          // Create new input field with same key and type
          // Include object_keys for object type fields to enable key extraction dropdown
          const newField: FieldDefinition = {
            key: output.key,
            type: output.type,
            label: output.label || output.key,
            required: false,
            indexed: false,
            ...(output.object_keys && { object_keys: output.object_keys }),
          };
          newFields.push(newField);
          existingFieldKeys.add(output.key);
        }

        // Create mapping for this field only if NO mapping exists
        // NEVER overwrite user-defined mappings
        const hasExistingMapping = existingMappingKeys.has(output.key) ||
          newMappings.some((m) => m.input_field_key === output.key);
        if (!hasExistingMapping) {
          newMappings.push({
            input_field_key: output.key,
            source_node_key: source.node_key,
            source_output_key: output.key,
            is_auto_mapped: true,
          });
        }
      }
    }

    return { newFields, newMappings };
  }, [nodeKey, availableSources, customInputFields, inputMappings]);

  // Update input mappings
  const updateInputMappings = useCallback(
    (mappings: InputFieldMapping[]) => {
      if (!nodeId) return;
      updateNodeData(nodeId, { input_mappings: mappings });
    },
    [nodeId, updateNodeData]
  );

  // Add custom input field
  // Note: keys are case-insensitive, always stored as lowercase
  const addInputField = useCallback(
    (field: FieldDefinition) => {
      if (!nodeId || !nodeData) return;
      const normalizedField = { ...field, key: field.key.toLowerCase() };
      const current = nodeData.input_fields || [];
      updateNodeData(nodeId, { input_fields: [...current, normalizedField] });
    },
    [nodeId, nodeData, updateNodeData]
  );

  // Add multiple custom input fields at once
  // Note: keys are case-insensitive, always stored as lowercase
  const addInputFields = useCallback(
    (fields: FieldDefinition[]) => {
      if (!nodeId || !nodeData || fields.length === 0) return;
      const normalizedFields = fields.map((f) => ({ ...f, key: f.key.toLowerCase() }));
      const current = nodeData.input_fields || [];
      updateNodeData(nodeId, { input_fields: [...current, ...normalizedFields] });
    },
    [nodeId, nodeData, updateNodeData]
  );

  // Remove custom input field
  const removeInputField = useCallback(
    (fieldKey: string) => {
      if (!nodeId || !nodeData) return;
      const current = nodeData.input_fields || [];
      updateNodeData(nodeId, {
        input_fields: current.filter((f) => f.key !== fieldKey),
      });
    },
    [nodeId, nodeData, updateNodeData]
  );

  // Update custom input field (rename key/label)
  // Note: keys are case-insensitive, always stored as lowercase
  const updateInputField = useCallback(
    (oldKey: string, newKey: string, newLabel?: string) => {
      if (!nodeId || !nodeData) return;
      const normalizedKey = newKey.toLowerCase();
      const current = nodeData.input_fields || [];
      const updated = current.map((f) => {
        if (f.key === oldKey) {
          return { ...f, key: normalizedKey, label: newLabel || newKey };
        }
        return f;
      });
      updateNodeData(nodeId, { input_fields: updated });

      // Also update any mappings that reference this field
      const currentMappings = nodeData.input_mappings || [];
      const updatedMappings = currentMappings.map((m) => {
        if (m.input_field_key === oldKey) {
          return { ...m, input_field_key: normalizedKey };
        }
        return m;
      });
      if (JSON.stringify(currentMappings) !== JSON.stringify(updatedMappings)) {
        updateNodeData(nodeId, { input_mappings: updatedMappings });
      }
    },
    [nodeId, nodeData, updateNodeData]
  );

  // Add custom output field
  const addOutputField = useCallback(
    (field: OutputFieldDefinition) => {
      if (!nodeId || !nodeData) return;
      const current = nodeData.output_fields || [];
      updateNodeData(nodeId, { output_fields: [...current, field] });
    },
    [nodeId, nodeData, updateNodeData]
  );

  // Remove custom output field
  const removeOutputField = useCallback(
    (fieldKey: string) => {
      if (!nodeId || !nodeData) return;
      const current = nodeData.output_fields || [];
      updateNodeData(nodeId, {
        output_fields: current.filter((f) => f.key !== fieldKey),
      });
    },
    [nodeId, nodeData, updateNodeData]
  );

  // Update custom output field (rename key/label)
  // Note: keys are case-insensitive, always stored as lowercase
  const updateOutputField = useCallback(
    (oldKey: string, newKey: string, newLabel?: string) => {
      if (!nodeId || !nodeData) return;
      const normalizedKey = newKey.toLowerCase();
      const current = nodeData.output_fields || [];
      const updated = current.map((f) => {
        if (f.key === oldKey) {
          return { ...f, key: normalizedKey, label: newLabel || newKey };
        }
        return f;
      });
      updateNodeData(nodeId, { output_fields: updated });
    },
    [nodeId, nodeData, updateNodeData]
  );

  // Update output field's value source (which input field provides the value)
  const updateOutputFieldSource = useCallback(
    (outputFieldKey: string, sourceInputKey: string | undefined) => {
      if (!nodeId || !nodeData) return;
      const current = nodeData.output_fields || [];
      const updated = current.map((f) => {
        if (f.key === outputFieldKey) {
          return { ...f, value_source_input_key: sourceInputKey };
        }
        return f;
      });
      updateNodeData(nodeId, { output_fields: updated });
    },
    [nodeId, nodeData, updateNodeData]
  );

  // Update a single mapping
  // IMPORTANT: When creating a manual mapping, we only remove existing MANUAL mappings
  // for the same key, preserving auto-mappings. This allows:
  // - "Input Fields" section to have auto-mappings (is_auto_mapped: true)
  // - "Config Options" section to have manual mappings (is_auto_mapped: false)
  // Both can coexist for the same key.
  const updateMapping = useCallback(
    (inputFieldKey: string, sourceNodeKey: string, sourceOutputKey: string, sourceKeyPath?: string) => {
      if (!nodeId) return;

      // Only remove manual mappings for this key, keep auto-mappings
      const current = inputMappings.filter((m) =>
        m.input_field_key !== inputFieldKey || m.is_auto_mapped === true
      );
      const newMapping: InputFieldMapping = {
        input_field_key: inputFieldKey,
        source_node_key: sourceNodeKey,
        source_output_key: sourceOutputKey,
        source_key_path: sourceKeyPath,
        is_auto_mapped: false, // Manual update
      };

      updateInputMappings([...current, newMapping]);
    },
    [nodeId, inputMappings, updateInputMappings]
  );

  // Update only the key path for an existing mapping
  const updateMappingKeyPath = useCallback(
    (inputFieldKey: string, keyPath: string | undefined) => {
      if (!nodeId) return;

      const current = inputMappings.map((m) => {
        if (m.input_field_key === inputFieldKey) {
          return { ...m, source_key_path: keyPath };
        }
        return m;
      });

      updateInputMappings(current);
    },
    [nodeId, inputMappings, updateInputMappings]
  );

  // Update literal value for an input field (manual/fixed value)
  const updateLiteralValue = useCallback(
    (inputFieldKey: string, literalValue: string) => {
      if (!nodeId) return;

      // Remove existing mapping for this field
      const current = inputMappings.filter((m) => m.input_field_key !== inputFieldKey);

      // Add new literal mapping
      const newMapping: InputFieldMapping = {
        input_field_key: inputFieldKey,
        source_node_key: LITERAL_SOURCE_KEY,
        source_output_key: 'value',
        literal_value: literalValue,
        is_auto_mapped: false,
      };

      updateInputMappings([...current, newMapping]);
    },
    [nodeId, inputMappings, updateInputMappings]
  );

  // Remove a mapping
  // IMPORTANT: Only removes MANUAL mappings, keeps auto-mappings intact
  // This ensures "Input Fields" auto-mappings are not affected by "Config Options" changes
  const removeMapping = useCallback(
    (inputFieldKey: string) => {
      if (!nodeId) return;
      // Only remove manual mappings for this key, keep auto-mappings
      updateInputMappings(inputMappings.filter((m) =>
        m.input_field_key !== inputFieldKey || m.is_auto_mapped === true
      ));
    },
    [nodeId, inputMappings, updateInputMappings]
  );

  return {
    // Data
    node,
    nodeKey,
    nodeType,
    nodeTypeSpec,
    inputFields,
    customInputFields, // Only user-defined fields (not defaults)
    outputFields,
    customOutputFields, // Only user-defined output fields
    schemaOutputFields, // Output fields derived from selected output schema
    inputMappings,
    availableSources,

    // Actions
    autoGenerateMappings,
    autoMapFromUpstream, // Create fields + mappings from upstream outputs
    updateInputMappings,
    updateMapping,
    updateMappingKeyPath,
    updateLiteralValue, // Set fixed/manual value
    removeMapping,
    addInputField,
    addInputFields, // Batch add multiple fields
    updateInputField, // Rename field key/label
    removeInputField,
    addOutputField,
    updateOutputField, // Rename output field key/label
    updateOutputFieldSource, // Set which input provides the output value
    removeOutputField,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

export function getNodeTypeSpec(nodeType: string): NodeTypeSpec | null {
  return NODE_TYPE_SPECS[nodeType] || null;
}

export function getAllNodeTypeSpecs(): NodeTypeSpec[] {
  return Object.values(NODE_TYPE_SPECS);
}
